import { Component } from 'react'
import { toast } from 'react-toastify'
import { IClientUID } from '../../../api/clients'
import { getClientSettings, updateClientSettings, restoreClientSettings, ISettings, SettingType, ProgramMode } from '../../../api/0x0001/settings'
import { getString, getLocale } from '../../../locale'
import Loader from '../../../components/loader'
import TemperatureInput from './temperature.input'
import PressureInput from './pressure.input'
import PercentInput from './percent.input'
import classnames from 'classnames'
import Slider, { Range } from 'rc-slider'
import 'rc-slider/assets/index.css'

type Props = {
    clientId: IClientUID
}

type State = {
    settings?: Record<keyof ISettings, string | number>
    updates: Partial<ISettings>
    errors: Partial<Record<keyof ISettings, string>>
    unsaved: boolean
}

class SettingsTab extends Component<Props, State> {
    state: State = {
        settings: undefined,
        updates: {},
        errors: {},
        unsaved: false,
    }

    componentDidMount = () => {
        this.loadSettings()
    }

    adjustSettings(settings: Partial<ISettings>) {
        if (settings.pumpMaxTime !== undefined) settings.pumpMaxTime /= 1000
        if (settings.relayCooldown !== undefined) settings.relayCooldown /= 1000
        if (settings.oneLiquidLevelDelay !== undefined) settings.oneLiquidLevelDelay /= 1000
        if (settings.pumpStartDelay !== undefined) settings.pumpStartDelay /= 1000
        if (settings.pumpStopDelay !== undefined) settings.pumpStopDelay /= 1000
        if (settings.fanMaxWorkTime !== undefined) settings.fanMaxWorkTime /= 1000
        if (settings.fanCoolTime !== undefined) settings.fanCoolTime /= 1000
        if (settings.circulationInterval !== undefined) settings.circulationInterval /= 1000
        if (settings.circulationWorkTime !== undefined) settings.circulationWorkTime /= 1000
        if (settings.circulationDelayStop !== undefined) settings.circulationDelayStop /= 1000
    }

    normalizeSettings(settings: Partial<ISettings>) {
        settings = { ...settings }
        if (settings.pumpMaxTime !== undefined) settings.pumpMaxTime *= 1000
        if (settings.relayCooldown !== undefined) settings.relayCooldown *= 1000
        if (settings.oneLiquidLevelDelay !== undefined) settings.oneLiquidLevelDelay *= 1000
        if (settings.pumpStartDelay !== undefined) settings.pumpStartDelay *= 1000
        if (settings.pumpStopDelay !== undefined) settings.pumpStopDelay *= 1000
        if (settings.fanMaxWorkTime !== undefined) settings.fanMaxWorkTime *= 1000
        if (settings.fanCoolTime !== undefined) settings.fanCoolTime *= 1000
        if (settings.circulationInterval !== undefined) settings.circulationInterval *= 1000
        if (settings.circulationWorkTime !== undefined) settings.circulationWorkTime *= 1000
        if (settings.circulationDelayStop !== undefined) settings.circulationDelayStop *= 1000
        return settings
    }

    loadSettings = async () => {
        try {
            const settings = (await getClientSettings(this.props.clientId)).data
            console.log('settings', settings)
            this.adjustSettings(settings)
            this.setState({ settings, updates: {}, errors: {}, unsaved: false })
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    async saveSettings() {
        try {
            const { updates, errors } = this.state
            if (Object.keys(updates).length === 0) {
                return toast.error(getString('settings.no_updates'))
            }
            if (Object.keys(errors).length > 0) {
                return toast.error(getString('settings.fix_errors'))
            }
            await updateClientSettings(
                this.props.clientId,
                Object.entries(this.normalizeSettings(updates)).map(([key, value]) => ({
                    type: SettingType[key as any] as any,
                    value: value!,
                })),
            )
            await this.loadSettings()
            toast.success(getString('common.success', 'common.saved'))
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    async restoreSettings() {
        try {
            await restoreClientSettings(this.props.clientId)
            await this.loadSettings()
            toast.success(getString('common.success', 'settings.restored'))
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    handleNumberChange(name: keyof ISettings, value: string | number) {
        const { settings, updates, errors } = this.state
        settings![name] = value
        if (typeof value === 'number' || /^\d+$/.test(value)) {
            updates[name] = Number(value)
            delete errors[name]
        } else {
            errors[name] = getString('settings.must_be_numeric')
            delete updates[name]
        }
        this.setState({ settings, updates, unsaved: true })
    }

    render() {
        if (this.state.settings === undefined) return <Loader size={14} />
        return (
            <div>
                {this.renderActions()}
                {this.renderCategories()}
            </div>
        )
    }

    renderActions() {
        return (
            <div className="grid grid-cols-4 gap-5">
                {this.renderActionButton(getString('common.save'), this.saveSettings.bind(this))}
                {this.renderActionButton(getString('settings.refresh'), this.loadSettings.bind(this))}
                {this.renderActionButton(getString('settings.restore'), this.restoreSettings.bind(this))}
                {this.renderUnsavedChanges()}
            </div>
        )
    }

    renderActionButton(label: string, onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) {
        return (
            <button
                onClick={onClick}
                className="group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-501"
            >
                {label}
            </button>
        )
    }

    renderUnsavedChanges() {
        if (!this.state.unsaved) return undefined
        return <div className="dark:text-white flex items-center">* {getString('common.unsaved_changes')}</div>
    }

    renderCategories() {
        return (
            <div className="">
                {this.renderGeneralCategory()}
                {this.renderSodaUnitCategory()}
                {this.renderCoolingUnitCategory()}
                {this.renderAmbientUnitCategory()}
                {this.renderCirculationPumpCategory()}
                {this.renderBoilerCategory()}
            </div>
        )
    }

    renderCategoryHeader(label: string) {
        return <h3 className="text-2xl dark:text-white font-semibold text-center">{label}</h3>
    }

    renderInputErrorMessage(message: string) {
        return <div className="text-red-500 text-sm">{message}</div>
    }

    //#region General Category
    renderGeneralCategory() {
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.general_settings'))}
                {this.renderProgramMode()}
                {this.renderWifiEnable()}
            </div>
        )
    }
    renderProgramMode() {
        const { settings } = this.state
        return [
            <label key={0} className="dark:text-white text-sm">
                {getString('settings.program_mode')}
            </label>,
            <select
                key={1}
                className={classnames('pretty-input', getLocale() === 'he' && 'pretty-input-rtl')}
                value={settings!.programMode}
                onInput={(e: any) => this.handleNumberChange('programMode', e.target.value)}
            >
                <option value={ProgramMode.AluminiumBlock}>{getString('settings.aluminium_block')}</option>
                <option value={ProgramMode.IceBank}>{getString('settings.ice_bank')}</option>
            </select>,
        ]
    }
    renderWifiEnable() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.wifiEnable}
                    onChange={(e: any) => this.handleNumberChange('wifiEnable', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.wifi_enable')}</label>
            </div>
        )
    }
    //#endregion

    //#region Soda Unit Category
    renderSodaUnitCategory() {
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.pump_settings'))}
                {this.renderLiquidLevelDisabled()}
                {this.renderPumpMaxWorkTime()}
                {this.renderPumpMinWaterPressure()}
                {this.renderPumpMinCo2Pressure()}
                {this.renderPumpStartDelay()}
                {this.renderPumpStopDelay()}
                {this.renderOneLiquidLevel()}
                {this.renderOneLiquidLevelDelay()}
                {this.renderIgnoreH2oPiezo()}
                {this.renderIgnoreCo2Piezo()}
            </div>
        )
    }
    renderLiquidLevelDisabled() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!settings!.liquidLevelDisabled}
                    onChange={(e: any) => this.handleNumberChange('liquidLevelDisabled', e.target.checked ? 0 : 1)}
                />
                <label className="dark:text-white text-sm">{getString('settings.liquid_level_enabled')}</label>
            </div>
        )
    }
    renderPumpPwmDuty() {
        const { settings, errors } = this.state
        const error = errors.pumpPwmDuty !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.pump_pwm_duty')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.pumpPwmDuty}
                    onInput={(e: any) => this.handleNumberChange('pumpPwmDuty', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={100}
                    value={Number(settings!.pumpPwmDuty)}
                    onChange={value => this.handleNumberChange('pumpPwmDuty', value)}
                />
                {error && this.renderInputErrorMessage(errors.pumpPwmDuty!)}
            </div>
        )
    }
    renderPumpMaxWorkTime() {
        const { settings, errors } = this.state
        const error = errors.pumpMaxTime !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.pump_max_time')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.pumpMaxTime}
                    onInput={(e: any) => this.handleNumberChange('pumpMaxTime', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.pumpMaxTime)}
                    onChange={value => this.handleNumberChange('pumpMaxTime', value)}
                />
                {error && this.renderInputErrorMessage(errors.pumpMaxTime!)}
            </div>
        )
    }
    renderPumpMinWaterPressure() {
        const { settings, errors } = this.state
        const error = errors.minH2oPiezo !== undefined
        return (
            <>
                <PressureInput
                    className="mt-4"
                    label={getString('settings.min_h2o_piezo')}
                    value={settings!.minH2oPiezo}
                    onInput={value => this.handleNumberChange('minH2oPiezo', value)}
                    error={error}
                    errorRenderer={() => error && this.renderInputErrorMessage(errors.minH2oPiezo!)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    value={Number(settings!.minH2oPiezo)}
                    min={1}
                    max={3299}
                    onChange={value => this.handleNumberChange('minH2oPiezo', value)}
                />
            </>
        )
    }
    renderPumpMinCo2Pressure() {
        const { settings, errors } = this.state
        const error = errors.minCo2Piezo !== undefined
        return (
            <>
                <PressureInput
                    className="mt-4"
                    label={getString('settings.min_co2_piezo')}
                    value={settings!.minCo2Piezo}
                    onInput={value => this.handleNumberChange('minCo2Piezo', value)}
                    error={error}
                    errorRenderer={() => error && this.renderInputErrorMessage(errors.minCo2Piezo!)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    value={Number(settings!.minCo2Piezo)}
                    min={1}
                    max={3299}
                    onChange={value => this.handleNumberChange('minCo2Piezo', value)}
                />
            </>
        )
    }
    renderPiezo1Calib() {
        const { settings, errors } = this.state
        const error = errors.piezo1Calib !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.piezo1_calib')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.piezo1Calib}
                    onInput={(e: any) => this.handleNumberChange('piezo1Calib', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.piezo1Calib!)}
            </div>
        )
    }
    renderPiezo2Calib() {
        const { settings, errors } = this.state
        const error = errors.piezo2Calib !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.piezo2_calib')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.piezo2Calib}
                    onInput={(e: any) => this.handleNumberChange('piezo2Calib', e.target.value)}
                />
                {error && this.renderInputErrorMessage(errors.piezo2Calib!)}
            </div>
        )
    }
    renderPumpStartDelay() {
        const { settings, errors } = this.state
        const error = errors.pumpStartDelay !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.pump_start_delay')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.pumpStartDelay}
                    onInput={(e: any) => this.handleNumberChange('pumpStartDelay', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={30}
                    value={Number(settings!.pumpStartDelay)}
                    onChange={value => this.handleNumberChange('pumpStartDelay', value)}
                />
                {error && this.renderInputErrorMessage(errors.pumpStartDelay!)}
            </div>
        )
    }
    renderPumpStopDelay() {
        const { settings, errors } = this.state
        const error = errors.pumpStopDelay !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.pump_stop_delay')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.pumpStopDelay}
                    onInput={(e: any) => this.handleNumberChange('pumpStopDelay', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={30}
                    value={Number(settings!.pumpStopDelay)}
                    onChange={value => this.handleNumberChange('pumpStopDelay', value)}
                />
                {error && this.renderInputErrorMessage(errors.pumpStopDelay!)}
            </div>
        )
    }
    renderOneLiquidLevel() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.oneLiquidLevel}
                    onChange={(e: any) => this.handleNumberChange('oneLiquidLevel', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.one_liq_lev')}</label>
            </div>
        )
    }
    renderOneLiquidLevelDelay() {
        const { settings, errors } = this.state
        if (!settings!.oneLiquidLevel) return undefined
        const error = errors.oneLiquidLevelDelay !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.one_liq_lev_delay')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.oneLiquidLevelDelay}
                    onInput={(e: any) => this.handleNumberChange('oneLiquidLevelDelay', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={30}
                    value={Number(settings!.oneLiquidLevelDelay)}
                    onChange={value => this.handleNumberChange('oneLiquidLevelDelay', value)}
                />
                {error && this.renderInputErrorMessage(errors.oneLiquidLevelDelay!)}
            </div>
        )
    }
    renderIgnoreH2oPiezo() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.ignoreH2oPiezo}
                    onChange={(e: any) => this.handleNumberChange('ignoreH2oPiezo', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.ignore_h2o_piezo')}</label>
            </div>
        )
    }
    renderIgnoreCo2Piezo() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.ignoreCo2Piezo}
                    onChange={(e: any) => this.handleNumberChange('ignoreCo2Piezo', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.ignore_co2_piezo')}</label>
            </div>
        )
    }
    //#endregion

    //#region Cooling Unit Category
    renderCoolingUnitCategory() {
        const { settings } = this.state
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.cooling_unit_settings'))}
                {settings!.programMode === ProgramMode.AluminiumBlock ? (
                    <>
                        <div className="mt-4 grid grid-cols-2 gap-8">
                            {this.renderNtc1MinValue()}
                            {this.renderNtc1MaxValue()}
                        </div>
                        <Range
                            reverse={getLocale() !== 'he'}
                            value={[Number(settings!.ntc1MaxValue), Number(settings!.ntc1MinValue)]}
                            min={1}
                            max={3299}
                            onChange={value => {
                                this.handleNumberChange('ntc1MaxValue', value[0])
                                this.handleNumberChange('ntc1MinValue', value[1])
                            }}
                        />
                    </>
                ) : (
                    <>
                        <div className="mt-4 grid grid-cols-2 gap-8">
                            {this.renderWaterValue()}
                            {this.renderIceValue()}
                        </div>
                        <Range
                            reverse={getLocale() === 'he'}
                            value={[Number(settings!.waterValue), Number(settings!.iceValue)]}
                            min={1}
                            max={3299}
                            onChange={value => {
                                this.handleNumberChange('waterValue', value[0])
                                this.handleNumberChange('iceValue', value[1])
                            }}
                        />
                    </>
                )}
                {this.renderRelayCooldown()}
            </div>
        )
    }
    renderNtc1MaxValue() {
        const { settings, errors } = this.state
        const error = errors.ntc1MaxValue !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.ntc1_max_value')}
                value={settings!.ntc1MaxValue}
                onInput={value => this.handleNumberChange('ntc1MaxValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.ntc1MaxValue!)}
            />
        )
    }
    renderNtc1MinValue() {
        const { settings, errors } = this.state
        const error = errors.ntc1MinValue !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.ntc1_min_value')}
                value={settings!.ntc1MinValue}
                onInput={value => this.handleNumberChange('ntc1MinValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.ntc1MinValue!)}
            />
        )
    }
    renderWaterValue() {
        const { settings, errors } = this.state
        const error = errors.waterValue !== undefined
        return (
            <PercentInput
                className="mt-4"
                label={getString('settings.water_value')}
                value={settings!.waterValue}
                onInput={value => this.handleNumberChange('waterValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.waterValue!)}
            />
        )
    }
    renderIceValue() {
        const { settings, errors } = this.state
        const error = errors.iceValue !== undefined
        return (
            <PercentInput
                className="mt-4"
                label={getString('settings.ice_value')}
                value={settings!.iceValue}
                onInput={value => this.handleNumberChange('iceValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.iceValue!)}
            />
        )
    }
    renderRelayCooldown() {
        const { settings, errors } = this.state
        const error = errors.relayCooldown !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.compressor_cooldown')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.relayCooldown}
                    onInput={(e: any) => this.handleNumberChange('relayCooldown', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.relayCooldown)}
                    onChange={value => this.handleNumberChange('relayCooldown', value)}
                />
                {error && this.renderInputErrorMessage(errors.relayCooldown!)}
            </div>
        )
    }
    //#endregion

    //#region Fan Category
    renderAmbientUnitCategory() {
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.ambient_unit_settings'))}
                {this.renderAmbientUnitEnable()}
                {this.renderNtc2Range()}
                {this.renderFanMaxWorkTime()}
                {this.renderFanCoolTime()}
            </div>
        )
    }
    renderAmbientUnitEnable() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.ambientMonitorEnabled}
                    onChange={(e: any) => this.handleNumberChange('ambientMonitorEnabled', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.ambient_monitor_enabled')}</label>
            </div>
        )
    }
    renderNtc2Range() {
        const { settings } = this.state
        return (
            <>
                <div className="mt-4 grid grid-cols-2 gap-8">
                    {this.renderNtc2MinValue()}
                    {this.renderNtc2MaxValue()}
                </div>
                <Range
                    reverse={getLocale() !== 'he'}
                    value={[Number(settings!.ntc2MaxValue), Number(settings!.ntc2MinValue)]}
                    min={1}
                    max={3299}
                    onChange={value => {
                        this.handleNumberChange('ntc2MaxValue', value[0])
                        this.handleNumberChange('ntc2MinValue', value[1])
                    }}
                />
            </>
        )
    }
    renderNtc2MaxValue() {
        const { settings, errors } = this.state
        const error = errors.ntc2MaxValue !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.ntc2_max_value')}
                value={settings!.ntc2MaxValue}
                onInput={value => this.handleNumberChange('ntc2MaxValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.ntc2MaxValue!)}
            />
        )
    }
    renderNtc2MinValue() {
        const { settings, errors } = this.state
        const error = errors.ntc2MinValue !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.ntc2_min_value')}
                value={settings!.ntc2MinValue}
                onInput={value => this.handleNumberChange('ntc2MinValue', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.ntc2MinValue!)}
            />
        )
    }
    renderFanMaxWorkTime() {
        const { settings, errors } = this.state
        const error = errors.fanMaxWorkTime !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.fan_max_work_time')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.fanMaxWorkTime}
                    onInput={(e: any) => this.handleNumberChange('fanMaxWorkTime', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.fanMaxWorkTime)}
                    onChange={value => this.handleNumberChange('fanMaxWorkTime', value)}
                />
                {error && this.renderInputErrorMessage(errors.fanMaxWorkTime!)}
            </div>
        )
    }
    renderFanCoolTime() {
        const { settings, errors } = this.state
        const error = errors.fanCoolTime !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.fan_cool_time')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.fanCoolTime}
                    onInput={(e: any) => this.handleNumberChange('fanCoolTime', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.fanCoolTime)}
                    onChange={value => this.handleNumberChange('fanCoolTime', value)}
                />
                {error && this.renderInputErrorMessage(errors.fanCoolTime!)}
            </div>
        )
    }
    //#endregion

    //#region Circulation Pump Category
    renderCirculationPumpCategory() {
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.circulation_pump_settings'))}
                {this.renderCirculationEnabled()}
                {this.renderCirculationDelayStop()}
                {this.renderCirculationInterval()}
                {this.renderCirculationWorkTime()}
            </div>
        )
    }
    renderCirculationEnabled() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.circulationEnabled}
                    onChange={(e: any) => this.handleNumberChange('circulationEnabled', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.circulation_enabled')}</label>
            </div>
        )
    }
    renderCirculationDelayStop() {
        const { settings, errors } = this.state
        const error = errors.circulationDelayStop !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.circulation_delay_stop')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.circulationDelayStop}
                    onInput={(e: any) => this.handleNumberChange('circulationDelayStop', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.circulationDelayStop)}
                    onChange={value => this.handleNumberChange('circulationDelayStop', value)}
                />
                {error && this.renderInputErrorMessage(errors.circulationDelayStop!)}
            </div>
        )
    }
    renderCirculationInterval() {
        const { settings, errors } = this.state
        const error = errors.circulationInterval !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.circulation_interval')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.circulationInterval}
                    onInput={(e: any) => this.handleNumberChange('circulationInterval', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={86400}
                    value={Number(settings!.circulationInterval)}
                    onChange={value => this.handleNumberChange('circulationInterval', value)}
                />
                {error && this.renderInputErrorMessage(errors.circulationInterval!)}
            </div>
        )
    }
    renderCirculationWorkTime() {
        const { settings, errors } = this.state
        const error = errors.circulationWorkTime !== undefined
        return (
            <div className="mt-4">
                <label className="dark:text-white text-sm">{getString('settings.circulation_work_time')}</label>
                <input
                    type="number"
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings!.circulationWorkTime}
                    onInput={(e: any) => this.handleNumberChange('circulationWorkTime', e.target.value)}
                />
                <Slider
                    reverse={getLocale() === 'he'}
                    min={0}
                    max={2000}
                    value={Number(settings!.circulationWorkTime)}
                    onChange={value => this.handleNumberChange('circulationWorkTime', value)}
                />
                {error && this.renderInputErrorMessage(errors.circulationWorkTime!)}
            </div>
        )
    }
    //#endregion

    //#region Boiler Category
    renderBoilerCategory() {
        const { settings } = this.state
        return (
            <div className="top-5 bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                {this.renderCategoryHeader(getString('settings.boiler_settings'))}
                {this.renderBoilerEnable()}
                <div className="grid grid-cols-2 gap-8">
                    {this.renderBoilerMinHeat()}
                    {this.renderBoilerMaxHeat()}
                </div>
                <Range
                    reverse={getLocale() !== 'he'}
                    value={[Number(settings!.boilerMaxHeat), Number(settings!.boilerMinHeat)]}
                    min={1}
                    max={3299}
                    onChange={value => {
                        this.handleNumberChange('boilerMinHeat', value[1])
                        this.handleNumberChange('boilerMaxHeat', value[0])
                    }}
                />
            </div>
        )
    }
    renderBoilerEnable() {
        const { settings } = this.state
        return (
            <div className="mt-4 flex items-center">
                <input
                    type="checkbox"
                    className="border-gray-300 me-3"
                    checked={!!settings!.boilerEnable}
                    onChange={(e: any) => this.handleNumberChange('boilerEnable', e.target.checked ? 1 : 0)}
                />
                <label className="dark:text-white text-sm">{getString('settings.boiler_enable')}</label>
            </div>
        )
    }
    renderBoilerMaxHeat() {
        const { settings, errors } = this.state
        const error = errors.boilerMaxHeat !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.boiler_max_heat')}
                value={settings!.boilerMaxHeat}
                onInput={value => this.handleNumberChange('boilerMaxHeat', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.boilerMaxHeat!)}
            />
        )
    }
    renderBoilerMinHeat() {
        const { settings, errors } = this.state
        const error = errors.boilerMinHeat !== undefined
        return (
            <TemperatureInput
                className="mt-4"
                label={getString('settings.boiler_min_heat')}
                value={settings!.boilerMinHeat}
                onInput={value => this.handleNumberChange('boilerMinHeat', value)}
                error={error}
                errorRenderer={() => error && this.renderInputErrorMessage(errors.boilerMinHeat!)}
            />
        )
    }
    //#endregion
}

export default SettingsTab
